import { useMemo } from 'react';

import { type MessageDescriptor } from 'react-intl';

import { LLM_PAGE_TYPE_LABELS } from './LLM_PAGE_TYPE_LABELS';
import type { PageContextType } from './PageContextType';
import { shuffleArray } from './shuffleArray';

export const useGetLLMLabelWithoutRepeat = (
	pageContextType: PageContextType,
): {
	getLLMLabel:
		| (() => {
				labelText: undefined;
				currentLabelIndex: number;
		  })
		| (() => {
				labelText: {
					message: MessageDescriptor;
				};
				currentLabelIndex: number;
		  });
} => {
	/**
	 * To avoid repetition, this function shuffles the labels array
	 * and for every function call, it always get the next one in the shuffled array.
	 *
	 * Using `useMemo`, this function will reset the labelsShuffled when pageContextType changes.
	 * so it will always randomise the correct labels for the current pageContextType.
	 */
	const getLLMLabel = useMemo(() => {
		const labelsByPageType = LLM_PAGE_TYPE_LABELS[pageContextType];
		if (!labelsByPageType) {
			return (): {
				labelText: undefined;
				currentLabelIndex: number;
			} => ({ labelText: undefined, currentLabelIndex: -1 });
		}

		const shuffledLabels = shuffleArray(labelsByPageType);
		let labelIndex = 0;

		return (): {
			labelText: {
				message: MessageDescriptor;
			};
			currentLabelIndex: number;
		} => {
			const currentLabelIndex = labelIndex;
			const labelText = shuffledLabels[labelIndex];
			// make sure the index loops back to the beginning of the array
			labelIndex = (labelIndex + 1) % shuffledLabels.length;

			return { labelText, currentLabelIndex };
		};
	}, [pageContextType]);

	return {
		getLLMLabel,
	};
};
